import {
  FOLLOW_LOCATIONS,
  followVenue,
  unfollowVenue,
} from "@punchup/data/legacy";
import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { VenuePagesRow } from "../../data/venue_pages";
import { createClientComponentClient } from "@punchup/data/supabase/client";

function venueSubscriptionQueryKey(venueId: string) {
  return ["venue-subscription", venueId];
}

export function useVenueSubscription(
  venueId: string,
  initialIsFollowing: boolean
) {
  const { data: isFollowing } = useQuery({
    queryKey: venueSubscriptionQueryKey(venueId),
    queryFn: () => initialIsFollowing,
    initialData: initialIsFollowing,
    staleTime: Infinity,
  });

  return { isFollowing: isFollowing ?? initialIsFollowing };
}

interface VenueSubscriptionMutationParams {
  action: "follow" | "unfollow";
  location: FOLLOW_LOCATIONS;
}

export function useVenueSubscriptionMutation(
  venue: Pick<VenuePagesRow, "id" | "slug">
): UseMutationResult<
  void,
  Error,
  VenueSubscriptionMutationParams,
  { previous: boolean | undefined }
> {
  const queryClient = useQueryClient();
  const supabase = createClientComponentClient();
  const queryKey = venueSubscriptionQueryKey(venue.id);

  return useMutation({
    mutationFn: async ({
      action,
      location,
    }: VenueSubscriptionMutationParams) => {
      if (action === "follow") {
        await followVenue(supabase, venue, location);
      } else {
        await unfollowVenue(supabase, venue, location);
      }
    },
    onMutate: async ({ action }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<boolean>(queryKey);
      queryClient.setQueryData(queryKey, action === "follow");
      return { previous };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });
}
